import React from "react";
import { ISourceAuction } from "../../../services";
import { UncontrolledQuantityInput } from "../components/UncontrolledQuantityInput";
import { ReturnActionButtons } from "../components/ReturnActionButtons";

/**
 * Renders a quantity input cell for the given auction
 */
export const renderQuantityInput = (
  auctionId: string,
  rowData: ISourceAuction[],
  editingAuctionId: string | null,
  quantities: Record<string, string>,
  handleSaveValue: (auctionId: string, value: string) => void,
  handleCancelEdit: () => void,
  isCancelling: boolean,
  activeInputRef: React.RefObject<HTMLInputElement | null>,
  handleStartEdit: (auctionId: string) => void,
  canSubmit: boolean
) => {
  const isEditing = editingAuctionId === auctionId;
  // All other rows should be disabled when one is being edited
  const isDisabled = editingAuctionId !== null && !isEditing;

  // Find the maximum capacity for this auction
  const auctionData = rowData.find((auction: ISourceAuction) => auction.auctionId === auctionId);
  const maxCapacity = auctionData?.availableAllocatedCapacity || 0;

  return (
    <UncontrolledQuantityInput
      auctionId={auctionId}
      isEditing={isEditing}
      isDisabled={isDisabled}
      canSubmit={canSubmit}
      initialValue={quantities[auctionId] || ""}
      onSave={(value) => handleSaveValue(auctionId, value)}
      onCancel={handleCancelEdit}
      isCancelling={isCancelling}
      inputRef={activeInputRef}
      maxCapacity={maxCapacity} // Pass maximum capacity
      onStartEdit={() => handleStartEdit(auctionId)} // Pass onStartEdit handler
    />
  );
};

/**
 * Renders the edit/save/cancel buttons for the given auction
 */
export const renderEditButton = (
  name: string,
  rowData: ISourceAuction[],
  getAuctionIdFromName: (name: string, rowData: ISourceAuction[]) => string,
  editingAuctionId: string | null,
  activeInputRef: React.RefObject<HTMLInputElement | null>,
  handleSaveValue: (auctionId: string, value: string) => void,
  handleCancelEdit: () => void,
  handleStartEdit: (auctionId: string) => void,
  setIsCancelling: (value: boolean) => void,
  canSubmit: boolean,
  handleSubmitReturn: () => void,
  quantities: Record<string, string>
) => {
  const auctionId = getAuctionIdFromName(name, rowData);

  const isEditing = editingAuctionId === auctionId;
  const isDisabled = editingAuctionId !== null && !isEditing;

  // Find the current auction data and check if quantity exceeds capacity
  const auctionData = rowData.find(
    (auction: ISourceAuction) => auction.auctionId === auctionId
  );
  const maxCapacity = auctionData?.availableAllocatedCapacity || 0;
  const currentValue = activeInputRef.current?.value || "";
  const exceedsCapacity =
    isEditing &&
    currentValue !== "" &&
    parseInt(currentValue, 10) > maxCapacity;

  // Check if this auction has a quantity value set
  const hasQuantity = Boolean(
    quantities[auctionId] && quantities[auctionId].trim() !== ""
  );

  return (
    <ReturnActionButtons
      auctionId={auctionId}
      isEditing={isEditing}
      isDisabled={isDisabled || !canSubmit}
      exceedsCapacity={exceedsCapacity}
      hasQuantity={hasQuantity}
      canSubmit={canSubmit}
      onSave={() => {
        if (activeInputRef.current) {
          const value = activeInputRef.current.value;
          handleSaveValue(auctionId, value);
        }
      }}
      onCancel={() => {
        // Set cancelling flag to prevent onBlur from saving
        setIsCancelling(true);
        setTimeout(() => {
          handleCancelEdit();
        }, 0);
      }}
      onStartEdit={() => handleStartEdit(auctionId)}
      onSubmit={handleSubmitReturn}
      inputRef={activeInputRef}
      maxCapacity={maxCapacity}
    />
  );
};